import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  IntlProvider,
  loadMessages,
  LocalizationProvider,
} from '@progress/kendo-react-intl';
import {
  Notification as KendoReactNotification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import { ProgressBar } from '@progress/kendo-react-progressbars';
import {
  Upload,
  UploadFileInfo,
  UploadOnAddEvent,
} from '@progress/kendo-react-upload';
import JSZip, { JSZipObject } from 'jszip';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { ChunkUploadResponse } from '../../Interfaces/ProductsManagement/ArtifactChunkUploadResponse';
import {
  ArtifactImportPayload,
  ProductArtifactItem,
  ProductPackage,
} from '../../Interfaces/ProductsManagement/ArtifactImportPayload';
import {
  ArtifactItem,
  ArtifactPackageHashMap,
  FilteredZipEntry,
} from '../../Interfaces/ProductsManagement/ArtifactPackageHashMap';
import { ApplicationState } from '../../store';
import {
  registerArtifactsImport,
  resetActivationFlags,
  validateArtifactVersions,
} from '../../store/ProductsManagement/ProductsActions';
import {
  ARCH_ALIASES,
  DEFAULT_CHUNK_SIZE_BYTES,
  FORM_DATA_KEYS,
  MAX_VISIBLE_UPLOAD_NOTIFICATIONS,
  VALID_COMPONENT_TYPES,
  XML_SCHEMA,
} from '../../utils/constants';
import esMessages from '../../utils/gridMessageKey.json';
import { LocaleInterface, locales } from '../AlertsGrid/GridLocale';
import { GetString } from '../StringResourceManager';
import ProductPackageVersionsGrid from './ProductPackageVersionGrid';

loadMessages(esMessages, 'fr-CA');

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    headerLabelGrid: {
      width: '90%',
      marginTop: 10,
      marginLeft: 10,
      marginBottom: 10,
    },
    importButton: {
      maxWidth: '500px',
      marginLeft: 10,
      marginBottom: 10,
    },
    validationDialog: {
      alignItems: 'center',
    },
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    packageGrid: {
      height: '400px',
      marginTop: 24,
      marginLeft: 10,
      marginBottom: 10,
      marginRight: 10,
    },
  }),
);

type ZipValidationResult = {
  valid: boolean;
  validArtifacts: ArtifactPackageHashMap[];
  missingFiles?: string[];
  hashMismatchFiles?: string[];
  requiredFiles?: string[];
  preparedDbPayload?: ArtifactImportPayload;
};

type FileProgress = {
  fileName: string;
  progress: number;
  status: ApiEnums.FileUploadStatus;
};

const ProductsContainer: React.FC = ({}): React.ReactElement => {
  const classes = useStyles();
  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );
  const artifacts: ArtifactPackageHashMap[] = useSelector(
    (state: ApplicationState) => state.productsReducer.artifactNameVersionMap,
  );
  const activationSuccess = useSelector(
    (state: ApplicationState) =>
      state.productsReducer.activatePackageVersionSuccess,
  );

  const activationFailed = useSelector(
    (state: ApplicationState) =>
      state.productsReducer.activatePackageVersionFailed,
  );

  const uploadRef = useRef<Upload>(null);
  const dispatch: Dispatch<any> = useDispatch();

  const [currentLocale, setCurrentLocale] = useState<LocaleInterface>(
    locales[0],
  );
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [uploadedZipFile, setUploadedZipFile] = useState<File | null>(null);
  const [validationState, setValidationState] =
    useState<ApiEnums.FileValidationState>(ApiEnums.FileValidationState.Idle);
  const [showConfirmationDialog, setShowConfirmationDialog] =
    useState<boolean>(false);
  const [visibleFiles, setVisibleFiles] = useState<string[]>([]);
  const [fileProgressList, setFileProgressList] = useState<FileProgress[]>([]);
  const [preparedDbPayload, setPreparedDbPayload] =
    useState<ArtifactImportPayload | null>(null);
  const [filesToUpload, setFilesToUpload] = useState<string[]>([]);
  const [noFilesToUpload, setNoFilesToUpload] = React.useState(false);
  const [successNotification, setSuccessNotification] = React.useState(false);
  const [partialSuccessNotification, setPartialSuccessNotification] =
    React.useState(false);
  const [failureNotification, setFailureNotification] = React.useState(false);

  const VALIDATION_IN_PROGRESS = GetString('VALIDATION_IN_PROGRESS');
  const VALIDATION_FAILED = GetString('VALIDATION_FAILED');
  const UPLOAD_ARTIFACTS_CONFIRM_MESSAGE = GetString(
    'UPLOAD_ARTIFACTS_CONFIRM_MESSAGE',
  );
  const FILES_UPLOAD_LIST_MESSAGE = GetString('FILES_UPLOAD_LIST_MESSAGE');
  const NO_FILES_TO_UPLOAD_MESSAGE = GetString('NO_FILES_TO_UPLOAD_MESSAGE');
  const ACTIVATION_SUCCESS_MESSAGE = GetString('ACTIVATION_SUCCESS_MESSAGE');
  const ACTIVATION_FAILED_MESSAGE = GetString('ACTIVATION_FAILED_MESSAGE');
  const IMPORT_PRODUCT_PACKAGES_TITLE = GetString(
    'IMPORT_PRODUCT_PACKAGES_TITLE',
  );
  const UPLOADING_STATUS = GetString('UPLOADING_STATUS');
  const COMPLETED_STATUS = GetString('COMPLETED_STATUS');

  const IMPORT_COMPLETED = GetString('IMPORT_COMPLETED');
  const IMPORT_COMPLETED_WITH_ERRORS = GetString(
    'IMPORT_COMPLETED_WITH_ERRORS',
  );
  const IMPORT_FAILED = GetString('IMPORT_FAILED');
  const FAILED_STATUS = GetString('FAILED_STATUS');
  const VALIDATION_TITLE = GetString('VALIDATION_TITLE');
  const PLEASE_WAIT = GetString('PLEASE_WAIT');
  const CLOSE_STR = GetString('CLOSE_STR');
  const YES = GetString('YES');
  const NO = GetString('NO');

  useEffect(() => {
    if (
      uploadedZipFile &&
      validationState === ApiEnums.FileValidationState.Validating &&
      artifacts.length > 0
    ) {
      void showUploadConfirmation(uploadedZipFile);
    }
  }, [artifacts]);

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    const timers: any[] = [];

    fileProgressList.forEach(file => {
      if (
        visibleFiles.includes(file.fileName) &&
        (file.status === ApiEnums.FileUploadStatus.Completed ||
          file.status === ApiEnums.FileUploadStatus.Failed)
      ) {
        const timeoutMs =
          file.status === ApiEnums.FileUploadStatus.Completed ? 2000 : 3000;

        const timer = setTimeout(() => {
          setVisibleFiles(prev => prev.filter(name => name !== file.fileName));
        }, timeoutMs);

        timers.push(timer);
      }
    });

    return () => timers.forEach(clearTimeout);
  }, [fileProgressList, visibleFiles]);

  useEffect(() => {
    let timeoutId: any;

    if (activationSuccess || activationFailed) {
      timeoutId = setTimeout(() => {
        resetActivationFlags(dispatch);
      }, 3000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [activationSuccess, activationFailed]);

  useEffect(() => {
    let timeoutId: any;

    if (
      successNotification ||
      partialSuccessNotification ||
      failureNotification
    ) {
      timeoutId = setTimeout(() => {
        setSuccessNotification(false);
        setPartialSuccessNotification(false);
        setFailureNotification(false);
      }, 3000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [successNotification, partialSuccessNotification, failureNotification]);

  const normalizeZipPath = (value: string): string =>
    value.replace(/\\/g, '/').replace(/^\/+/, '').trim();

  const normalizeZipPathForCompare = (value: string): string =>
    normalizeZipPath(value).toLowerCase();

  const createClientId = (): string =>
    (crypto as any).randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

  const getRawFileFromUpload = (fileInfo: UploadFileInfo): File | null => {
    const rawFile = fileInfo.getRawFile?.();
    return rawFile instanceof File ? rawFile : null;
  };

  const getFileNameFromPath = (filePath: string): string => {
    const pathParts = normalizeZipPath(filePath).split('/').filter(Boolean);
    return pathParts[pathParts.length - 1] ?? '';
  };

  const getFileExtension = (fileName: string): string => {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex >= 0
      ? fileName.substring(lastDotIndex + 1).toLowerCase()
      : '';
  };

  const getAttribute = (node: Element, attributeName: string): string =>
    (node.getAttribute(attributeName) ?? '').trim();

  const parseXml = (xmlText: string): Document | null => {
    const cleanXml = xmlText.replace(/^\uFEFF/, '');
    const parser = new DOMParser();
    const xmlDocument = parser.parseFromString(cleanXml, 'application/xml');

    return xmlDocument.querySelector('parsererror') ? null : xmlDocument;
  };

  const isSupportedConfigXml = (xmlDocument: Document): boolean => {
    const rootNodeName = xmlDocument.documentElement.nodeName;

    if (rootNodeName !== XML_SCHEMA.rootNode) {
      return false;
    }

    const componentVersionNode = xmlDocument.querySelector(
      XML_SCHEMA.componentVersionSelector,
    );
    if (!componentVersionNode) {
      return false;
    }

    const productName = getAttribute(
      componentVersionNode,
      XML_SCHEMA.attributes.productName,
    );

    const productVersion = getAttribute(
      componentVersionNode,
      XML_SCHEMA.attributes.productVersion,
    );

    if (!productName || !productVersion) {
      return false;
    }

    return Boolean(xmlDocument.querySelector(XML_SCHEMA.componentSelector));
  };

  const findConfigXmlEntry = async (
    zip: JSZip,
  ): Promise<JSZipObject | null> => {
    const xmlEntries = Object.values(zip.files).filter(entry => {
      if (entry.dir) {
        return false;
      }

      const fileName = getFileNameFromPath(entry.name).toLowerCase();

      return /^merged.*\.xml$/i.test(fileName);
    });

    for (const entry of xmlEntries) {
      const xmlText = await entry.async('text');
      const xmlDocument = parseXml(xmlText);

      if (xmlDocument && isSupportedConfigXml(xmlDocument)) {
        return entry;
      }
    }

    return null;
  };

  const extractProductPackage = (
    xmlDocument: Document,
  ): ProductPackage | null => {
    const componentVersionNode = xmlDocument.querySelector(
      XML_SCHEMA.componentVersionSelector,
    );

    if (!componentVersionNode) {
      return null;
    }

    const productName = getAttribute(
      componentVersionNode,
      XML_SCHEMA.attributes.productName,
    );

    const productVersion = getAttribute(
      componentVersionNode,
      XML_SCHEMA.attributes.productVersion,
    );

    if (!productName || !productVersion) {
      return null;
    }

    return {
      Name: productName,
      Version: productVersion,
      Group: productName,
      IsActive: false,
    };
  };

  const extractRequiredComponents = (xmlDocument: Document): ArtifactItem[] => {
    const componentNodes = Array.from(
      xmlDocument.querySelectorAll(XML_SCHEMA.componentSelector),
    );

    return componentNodes
      .filter(
        node =>
          getAttribute(node, XML_SCHEMA.attributes.install).toLowerCase() ===
          'true',
      )
      .map(node => {
        const filePath = normalizeZipPath(
          getAttribute(node, XML_SCHEMA.attributes.fileName),
        );
        return {
          Name: getAttribute(node, XML_SCHEMA.attributes.name),
          Version: getAttribute(node, XML_SCHEMA.attributes.version),
          Type: getAttribute(node, XML_SCHEMA.attributes.type),
          Repository: getAttribute(node, XML_SCHEMA.attributes.repo),
          Package: getAttribute(node, XML_SCHEMA.attributes.package),
          FilePath: filePath,
          FileName: getFileNameFromPath(filePath),
          SHA256: 'sha256:' + getAttribute(node, XML_SCHEMA.attributes.sha256),
        };
      })
      .filter(
        component =>
          component.Name.length > 0 &&
          component.Version.length > 0 &&
          component.Type.length > 0 &&
          component.Repository.length > 0 &&
          component.Package.length > 0 &&
          component.FilePath.length > 0 &&
          component.FileName.length > 0 &&
          component.SHA256.length > 0 &&
          VALID_COMPONENT_TYPES.has(component.Type),
      );
  };

  const bufferToHex = (buffer: ArrayBuffer): string =>
    Array.from(new Uint8Array(buffer))
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');

  const normalizeSha256 = (value: string): string =>
    value
      .replace(/^sha256:/i, '')
      .trim()
      .toLowerCase();

  const computeSha256FromFileData = async (
    fileData: JSZip.JSZipObject,
  ): Promise<string> => {
    const fileBuffer = await fileData.async('arraybuffer');
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', fileBuffer);
    return bufferToHex(hashBuffer).toLowerCase();
  };

  const zipFilesLookup = (zip: JSZip): Map<string, JSZip.JSZipObject> => {
    const lookup = new Map<string, JSZip.JSZipObject>();

    Object.entries(zip.files).forEach(([entryName, entry]) => {
      if (!entry.dir) {
        lookup.set(normalizeZipPathForCompare(entryName), entry);
      }
    });

    return lookup;
  };

  const inferArchFromFileName = (fileName: string): string | null => {
    const archMatch = fileName
      .toLowerCase()
      .match(
        /(?:^|[_-])(linux-arm64|linux-amd64|arm64|amd64|x64)(?:[_\-.]|$)/i,
      );

    if (!archMatch) {
      return null;
    }

    const arch = archMatch[1].toLowerCase();
    return ARCH_ALIASES[arch] ?? arch;
  };

  const prepareDbPayload = (
    xmlDocument: Document,
    validFiles: ArtifactItem[],
  ): ArtifactImportPayload | null => {
    const productPackage = extractProductPackage(xmlDocument);

    if (!productPackage) {
      return null;
    }

    const productArtifactItems: ProductArtifactItem[] = validFiles.map(
      component => ({
        Type: component.Type,
        Repository: component.Repository,
        Package: component.Package,
        Version: component.Version,
        FileName: component.FileName,
        Arch: inferArchFromFileName(component.FileName),
        URI: null,
        SHA256: `sha256:${normalizeSha256(component.SHA256)}`,
      }),
    );

    return {
      ProductPackage: productPackage,
      ProductArtifactItems: productArtifactItems,
    };
  };

  const validateCustomConfigZip = async (
    file: File,
  ): Promise<ZipValidationResult> => {
    try {
      const zip = await JSZip.loadAsync(file);
      const configXmlEntry = await findConfigXmlEntry(zip);

      if (!configXmlEntry) {
        return {
          valid: false,
          validArtifacts: [],
        };
      }

      const xmlText = await configXmlEntry.async('text');
      const xmlDocument = parseXml(xmlText);

      if (!xmlDocument || !isSupportedConfigXml(xmlDocument)) {
        return {
          valid: false,
          validArtifacts: [],
        };
      }

      const requiredArtifacts = extractRequiredComponents(xmlDocument);

      if (requiredArtifacts.length === 0) {
        return {
          valid: false,
          validArtifacts: [],
        };
      }

      const zipFilesMap = zipFilesLookup(zip);

      const missingFiles = requiredArtifacts
        .filter(
          component =>
            !zipFilesMap.has(normalizeZipPathForCompare(component.FilePath)),
        )
        .map(component => `${component.Name}: ${component.FilePath}`);

      if (missingFiles.length > 0) {
        return {
          valid: false,
          validArtifacts: [],
          missingFiles,
        };
      }

      const validationResults = await Promise.all(
        requiredArtifacts.map(async component => {
          const fileData = zipFilesMap.get(
            normalizeZipPathForCompare(component.FilePath),
          );

          if (!fileData) {
            return {
              component,
              isHashValid: false,
              actualHash: '',
            };
          }

          const actualHash = await computeSha256FromFileData(fileData);

          return {
            component,
            isHashValid:
              normalizeSha256(actualHash) === normalizeSha256(component.SHA256),
            actualHash,
          };
        }),
      );

      const hashMismatchFiles = validationResults
        .filter(result => !result.isHashValid)
        .map(
          result => `${result.component.Name}: ${result.component.FilePath}`,
        );

      const validFiles = validationResults
        .filter(result => result.isHashValid)
        .map(result => result.component);

      const validArtifacts: ArtifactPackageHashMap[] = validFiles.map(
        component => ({
          Package: component.Package,
          Version: component.Version,
          SHA256: component.SHA256,
          Type: component.Type,
          FilePath: component.FilePath,
          Repository: component.Repository,
          Upload: false,
        }),
      );

      const preparedPayload = prepareDbPayload(xmlDocument, validFiles);

      if (!preparedPayload) {
        return {
          valid: false,
          validArtifacts: [],
        };
      }

      if (hashMismatchFiles.length > 0) {
        return {
          valid: false,
          validArtifacts,
          hashMismatchFiles,
          requiredFiles: validFiles.map(component => component.FilePath),
          preparedDbPayload: preparedPayload,
        };
      }

      return {
        valid: true,
        validArtifacts,
        requiredFiles: validFiles.map(component => component.FilePath),
        preparedDbPayload: preparedPayload,
      };
    } catch (error) {
      return {
        valid: false,
        validArtifacts: [],
      };
    }
  };

  const onFilesAdd = async (event: UploadOnAddEvent): Promise<void> => {
    const addedFile = event.affectedFiles[0];
    if (!addedFile) {
      setSelectedFiles([]);
      return;
    }
    const rawFile = getRawFileFromUpload(addedFile);
    if (!rawFile) {
      setSelectedFiles([]);
      setValidationState(ApiEnums.FileValidationState.Failed);
      return;
    }

    setValidationState(ApiEnums.FileValidationState.Validating);

    try {
      const validationResult = await validateCustomConfigZip(rawFile);
      if (!validationResult.valid) {
        setSelectedFiles([]);
        setValidationState(ApiEnums.FileValidationState.Failed);
        return;
      }
      setUploadedZipFile(rawFile);
      setPreparedDbPayload(validationResult.preparedDbPayload ?? null);

      await validateArtifactVersions(dispatch, validationResult.validArtifacts);

      setSelectedFiles(event.newState);
      setValidationState(ApiEnums.FileValidationState.Idle);
    } catch {
      setSelectedFiles([]);
      setValidationState(ApiEnums.FileValidationState.Failed);
    }
  };

  useEffect(() => {
    if (filesToUpload.length > 0 || noFilesToUpload) {
      setShowConfirmationDialog(true);
    }
  }, [filesToUpload, noFilesToUpload]);

  const showUploadConfirmation = async (zipFile: File): Promise<void> => {
    const files = await getFilesToUpload(zipFile);
    setFilesToUpload(files);
    setNoFilesToUpload(files.length === 0);
  };

  const getFilesToUpload = async (zipFile: File): Promise<string[]> => {
    const validatedMap = new Map(
      artifacts.map(artifact => [
        normalizeZipPathForCompare(artifact.FilePath),
        artifact,
      ]),
    );

    const zip = await JSZip.loadAsync(zipFile);

    const filesToUpload: string[] = [];

    for (const entryName of Object.keys(zip.files)) {
      const zipEntry = zip.files[entryName];

      if (zipEntry.dir) {
        continue;
      }

      const normalizedPath = normalizeZipPath(entryName);
      const normalizedPathKey = normalizeZipPathForCompare(normalizedPath);

      if (getFileExtension(normalizedPath) === 'xml') {
        continue;
      }

      const artifact = validatedMap.get(normalizedPathKey);

      if (!artifact) {
        continue;
      }

      filesToUpload.push(getFileNameFromPath(normalizedPath));
    }
    return filesToUpload;
  };

  const extractFilteredFiles = async (
    zipFile: File,
  ): Promise<FilteredZipEntry[]> => {
    const validatedMap = new Map(
      artifacts.map(artifact => [
        normalizeZipPathForCompare(artifact.FilePath),
        artifact,
      ]),
    );

    const zip = await JSZip.loadAsync(zipFile);
    const files: FilteredZipEntry[] = [];

    for (const entryName of Object.keys(zip.files)) {
      const zipEntry = zip.files[entryName];

      if (zipEntry.dir) {
        continue;
      }

      const normalizedPath = normalizeZipPath(entryName);
      const normalizedPathKey = normalizeZipPathForCompare(normalizedPath);

      if (getFileExtension(normalizedPath) === 'xml') {
        continue;
      }

      const artifact = validatedMap.get(normalizedPathKey);

      if (!artifact) {
        continue;
      }

      const blob = await zipEntry.async('blob');

      files.push({
        name: getFileNameFromPath(normalizedPath),
        blob,
        filePath: normalizedPath,
        type: artifact?.Type,
        repository: artifact?.Repository.toLowerCase(),
        package: artifact?.Package,
        sha256: artifact?.SHA256,
        version: artifact?.Version,
      });
    }
    return files;
  };

  const appendFormDataValue = (
    formData: FormData,
    key: string,
    value?: string | null,
  ): void => {
    if (value && value.trim().length > 0) {
      formData.append(key, value.trim());
    }
  };

  const buildChunkFormData = (
    file: FilteredZipEntry,
    uploadId: string,
    sessionId: string,
    chunkIndex: number,
    totalChunks: number,
    chunk: Blob,
  ): FormData => {
    const formData = new FormData();

    formData.append(FORM_DATA_KEYS.uploadId, uploadId);
    formData.append(FORM_DATA_KEYS.sessionId, sessionId);
    formData.append(FORM_DATA_KEYS.fileName, file.name);
    formData.append(FORM_DATA_KEYS.filePath, file.filePath);
    formData.append(FORM_DATA_KEYS.chunkIndex, chunkIndex.toString());
    formData.append(FORM_DATA_KEYS.totalChunks, totalChunks.toString());
    formData.append(FORM_DATA_KEYS.chunk, chunk, file.name);

    appendFormDataValue(formData, FORM_DATA_KEYS.type, file.type);
    appendFormDataValue(formData, FORM_DATA_KEYS.repository, file.repository);
    appendFormDataValue(formData, FORM_DATA_KEYS.version, file.version);
    appendFormDataValue(formData, FORM_DATA_KEYS.package, file.package);
    appendFormDataValue(formData, FORM_DATA_KEYS.sha256, file.sha256);

    return formData;
  };

  const uploadFileInChunks = async (
    file: FilteredZipEntry,
    onProgress: (percent: number) => void,
    sessionId: string,
    chunkSizeBytes = DEFAULT_CHUNK_SIZE_BYTES,
  ): Promise<ChunkUploadResponse | null> => {
    const uploadId = createClientId();
    const totalChunks = Math.ceil(file.blob.size / chunkSizeBytes);
    let finalResponse: ChunkUploadResponse | null = null;

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * chunkSizeBytes;
      const end = Math.min(start + chunkSizeBytes, file.blob.size);
      const chunk = file.blob.slice(start, end);
      const formData = buildChunkFormData(
        file,
        uploadId,
        sessionId,
        chunkIndex,
        totalChunks,
        chunk,
      );

      const response = await fetch('Products/UploadFileChunk', {
        method: 'POST',
        body: formData,
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(
          `Chunk upload failed for ${file.name} [${chunkIndex + 1}/${totalChunks}]`,
        );
      }

      if (chunkIndex === totalChunks - 1) {
        if (!responseText) {
          throw new Error(
            `Empty response received for final chunk of ${file.name}`,
          );
        }

        try {
          finalResponse = JSON.parse(responseText);
        } catch {
          throw new Error(
            `Invalid response received for final chunk of ${file.name}`,
          );
        }
      }

      const percent = Math.round(((chunkIndex + 1) / totalChunks) * 100);
      onProgress(percent);
    }

    return finalResponse;
  };

  const updateFileProgress = (
    fileName: string,
    progress: number,
    status?: FileProgress['status'],
  ): void => {
    setFileProgressList(prev =>
      prev.map(file =>
        file.fileName === fileName
          ? {
              ...file,
              progress,
              status: status ?? file.status,
            }
          : file,
      ),
    );
  };

  const uploadFilteredFiles = async (
    files: FilteredZipEntry[],
  ): Promise<void> => {
    if (files.length === 0) {
      return;
    }

    const sessionId = createClientId();

    setFileProgressList(
      files.map(file => ({
        fileName: file.name,
        progress: 0,
        status: ApiEnums.FileUploadStatus.Uploading,
      })),
    );

    let successCount = 0;
    let failedCount = 0;

    const visionIqFiles = files.filter(f =>
      f.name.toLowerCase().endsWith('.visioniq.yml'),
    );

    const otherFiles = files.filter(
      f => !f.name.toLowerCase().endsWith('.visioniq.yml'),
    );

    const orderedFiles = [...otherFiles, ...visionIqFiles];

    for (const file of orderedFiles) {
      setVisibleFiles(prev =>
        [...prev, file.name].slice(-MAX_VISIBLE_UPLOAD_NOTIFICATIONS),
      );

      try {
        const uploadResponse = await uploadFileInChunks(
          file,
          filePercent => updateFileProgress(file.name, filePercent),
          sessionId,
        );
        if (!uploadResponse) {
          throw new Error(`No upload response received for ${file.name}`);
        }

        if (!uploadResponse.Success) {
          throw new Error(
            uploadResponse.Message ?? `Upload failed for ${file.name}`,
          );
        }
        successCount++;
        updateFileProgress(file.name, 100, ApiEnums.FileUploadStatus.Completed);
      } catch (err) {
        failedCount++;
        console.error(err);
        updateFileProgress(file.name, 100, ApiEnums.FileUploadStatus.Failed);
      }
    }

    if (successCount > 0 && failedCount === 0) {
      setSuccessNotification(true);
      setPartialSuccessNotification(false);
      setFailureNotification(false);
    } else if (successCount > 0 && failedCount > 0) {
      setSuccessNotification(false);
      setPartialSuccessNotification(true);
      setFailureNotification(false);
    } else {
      setSuccessNotification(false);
      setPartialSuccessNotification(false);
      setFailureNotification(true);
    }

    if (preparedDbPayload) {
      registerArtifactsImport(dispatch, sessionId, preparedDbPayload);
    }
  };

  const handleCloseNotification = (fileName: string): void => {
    setVisibleFiles(prev => prev.filter(name => name !== fileName));
  };

  const handleUploadArtifacts = async (): Promise<void> => {
    setShowConfirmationDialog(false);
    setSelectedFiles([]);
    setFilesToUpload([]);

    if (!uploadedZipFile) {
      return;
    }

    try {
      const files = await extractFilteredFiles(uploadedZipFile);
      await uploadFilteredFiles(files);
    } catch (error) {
      console.error('Artifact upload failed:', error);
    }
  };

  const onFilesRemove = (event: UploadOnAddEvent): void => {
    setSelectedFiles(event.newState);
    setFilesToUpload([]);
    setPreparedDbPayload(null);
  };

  const closeValidationPopup = (): void => {
    setValidationState(ApiEnums.FileValidationState.Idle);
  };

  const closeConfirmationPopup = (): void => {
    setSelectedFiles([]);
    setFilesToUpload([]);
    setNoFilesToUpload(false);
    setShowConfirmationDialog(false);
  };

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <Typography className={classes.headerLabelGrid} variant="h6">
            {IMPORT_PRODUCT_PACKAGES_TITLE}
          </Typography>
          <div className={classes.importButton}>
            <Upload
              batch={false}
              ref={uploadRef}
              files={selectedFiles}
              autoUpload={false}
              showActionButtons={false}
              multiple={false}
              onAdd={onFilesAdd}
              onRemove={onFilesRemove}
            ></Upload>
          </div>

          <div className={classes.packageGrid}>
            <ProductPackageVersionsGrid />
          </div>

          <div className={classes.validationDialog}>
            {validationState === ApiEnums.FileValidationState.Validating && (
              <Dialog width={400} title={PLEASE_WAIT} closeIcon={false}>
                <Typography variant="body1">
                  {VALIDATION_IN_PROGRESS}
                </Typography>
              </Dialog>
            )}

            {validationState === ApiEnums.FileValidationState.Failed && (
              <Dialog
                width={400}
                title={VALIDATION_TITLE}
                onClose={closeValidationPopup}
              >
                <div>{VALIDATION_FAILED}</div>
                <DialogActionsBar>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Button
                      themeColor={'primary'}
                      type="button"
                      onClick={closeValidationPopup}
                    >
                      {CLOSE_STR}
                    </Button>
                  </div>
                </DialogActionsBar>
              </Dialog>
            )}
            {showConfirmationDialog && (
              <Dialog
                width={600}
                title={IMPORT_PRODUCT_PACKAGES_TITLE}
                onClose={closeConfirmationPopup}
              >
                {noFilesToUpload ? (
                  <>
                    <Typography variant="body1">
                      {NO_FILES_TO_UPLOAD_MESSAGE}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body1">
                      {UPLOAD_ARTIFACTS_CONFIRM_MESSAGE}
                    </Typography>
                    <div style={{ marginTop: 16 }}>
                      <Typography
                        variant="body1"
                        style={{ fontWeight: 'bold' }}
                      >
                        {FILES_UPLOAD_LIST_MESSAGE}
                      </Typography>
                      <ul>
                        {filesToUpload.map(file => (
                          <li key={file}>
                            <Typography variant="body2">{file}</Typography>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}

                <DialogActionsBar>
                  <div
                    style={{
                      display: 'flex',
                      gap: '32px',
                      justifyContent: 'center',
                    }}
                  >
                    {noFilesToUpload ? (
                      <>
                        <Button
                          themeColor={'primary'}
                          type="button"
                          onClick={closeConfirmationPopup}
                        >
                          {CLOSE_STR}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          style={{ width: '100px' }}
                          themeColor={'primary'}
                          type="button"
                          onClick={handleUploadArtifacts}
                        >
                          {YES}
                        </Button>
                        <Button
                          style={{ width: '100px' }}
                          themeColor={'primary'}
                          type="button"
                          onClick={closeConfirmationPopup}
                        >
                          {NO}
                        </Button>
                      </>
                    )}
                  </div>
                </DialogActionsBar>
              </Dialog>
            )}
          </div>
          <NotificationGroup className={classes.bottomRight}>
            {fileProgressList
              .filter(file => visibleFiles.includes(file.fileName))
              .map(file => (
                <KendoReactNotification
                  key={file.fileName}
                  type={{
                    style:
                      file.status === ApiEnums.FileUploadStatus.Failed
                        ? 'error'
                        : file.status === ApiEnums.FileUploadStatus.Completed
                          ? 'success'
                          : 'info',
                    icon: true,
                  }}
                  closable={true}
                  onClose={() => handleCloseNotification(file.fileName)}
                >
                  <div style={{ width: 280 }}>
                    <Typography
                      variant="body2"
                      style={{
                        marginBottom: 6,
                        fontWeight: 500,
                      }}
                    >
                      {file.fileName}
                    </Typography>

                    <ProgressBar
                      value={file.progress}
                      max={100}
                      labelVisible={true}
                    />

                    <Typography variant="caption" style={{ marginTop: 4 }}>
                      {file.status === ApiEnums.FileUploadStatus.Uploading &&
                        `${UPLOADING_STATUS} ${file.progress}%`}
                      {file.status === ApiEnums.FileUploadStatus.Completed &&
                        COMPLETED_STATUS}
                      {file.status === ApiEnums.FileUploadStatus.Failed &&
                        FAILED_STATUS}
                    </Typography>
                  </div>
                </KendoReactNotification>
              ))}
            {activationSuccess && (
              <KendoReactNotification
                type={{
                  style: 'success',
                  icon: true,
                }}
              >
                {ACTIVATION_SUCCESS_MESSAGE}
              </KendoReactNotification>
            )}
            {activationFailed && (
              <KendoReactNotification
                type={{
                  style: 'error',
                  icon: true,
                }}
              >
                {ACTIVATION_FAILED_MESSAGE}
              </KendoReactNotification>
            )}
            {successNotification && (
              <KendoReactNotification
                type={{
                  style: 'success',
                  icon: true,
                }}
              >
                {IMPORT_COMPLETED}
              </KendoReactNotification>
            )}
            {partialSuccessNotification && (
              <KendoReactNotification
                type={{
                  style: 'warning',
                  icon: true,
                }}
              >
                {IMPORT_COMPLETED_WITH_ERRORS}
              </KendoReactNotification>
            )}
            {failureNotification && (
              <KendoReactNotification
                type={{
                  style: 'error',
                  icon: true,
                }}
              >
                {IMPORT_FAILED}
              </KendoReactNotification>
            )}
          </NotificationGroup>
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default ProductsContainer;
